// @ts-strict-ignore
import {
    ChecklistItemPlacement,
    ChecklistItemReleaseTypes,
    ChecklistItemTemplate,
    ReleaseStepType,
    SubscriptionFeatureType,
    User,
    UserGroupId,
} from '../types';
import * as React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import { forEach, forIn, isEqual, unionWith } from 'lodash';
import { getError, getIsLoading } from '../helpers';
import CreateUpdateChecklistItemModal from './CreateEditChecklistItemModal';
import { RootState } from '../reducers/reducers';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ReleaseStepDeleteChecklistItemRequest, tryUpdateAppSettingsV2 } from '../actions/appSettings';
import { AppComponentProps, NumberKeyedDictionary } from '../typescriptHelpers';
import { AppSettingsV2Updatable } from '../actions/updatable';
import styles from './styles/settingsStyles';
import Table, { ColumnType } from 'antd/lib/table';
import ChecklistItemAdditionalInfoModal, { ChecklistItemInfo } from './ChecklistItemAdditionalInfoModal';
import {
    displayTextForReleaseStepType,
    displayTextForUserChecklistGroups,
    displayUserNameLong,
} from '../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import {
    selectApp,
    selectAppPlatform,
    selectAppSettingChecklistItemData,
    selectAppStoreType,
    selectCLIUsers,
    selectDefaultReleaseStepsForAppForChecklists,
    selectIsWorkflowsCIStepEnabled,
    selectOrgGroups,
    selectStepConfigDataFromStepGraphs,
} from '../selectors/selectors';
import CLIEditActions from './checklistItems/CLIEditActionsComponent';
import { antGrey, runwayColors } from './styles/globalStyles';
import Tooltip from 'antd/lib/tooltip';
import { OwnerAvatar, OwnerAvatarGroup } from './Avatars';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { InlineStepConfigDescription } from '@/components/common/InlineStepConfigDescription';
import { useCallUpdatableForCollections } from '@/utils/hooks/helpers';
import { ChecklistItemTemplateEntityRequest } from '@/actions/entities';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { orgLevelCLIIcon } from './ChecklistItemRowComponent';

interface ChecklistItemsProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

interface ChecklistItemData extends ChecklistItemTemplate {
    disabled?: boolean;
}

export const ChecklistItemsComponent: React.FC<ChecklistItemsProps> = (props) => {
    const dispatch = useDispatch();
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const releaseSteps = useSelector((state: RootState) =>
        selectDefaultReleaseStepsForAppForChecklists(state, props.appId),
    );
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );
    const checklistItems = useSelector((state: RootState) => {
        return selectAppSettingChecklistItemData(state, props.appId);
    }) as ChecklistItemData[];

    const workflowId = useSelector((state: RootState) => selectApp(state, props.appId)?.workflowId);

    // make an array keeping a reference to original CLIs but sort them by date
    const sortedChecklistItems = checklistItems.sort((item1, item2) => (item1.createdAt < item2.createdAt ? -1 : 1));

    // update `disabled` property on CLIs if appropriate
    const groupedChecklistItems: Record<string, ChecklistItemData[]> = {
        [ReleaseStepType.Kickoff]: [],
        [ReleaseStepType.FeatureReadiness]: [],
        [ReleaseStepType.ReleaseCandidate]: [],
        [ReleaseStepType.RegressionTesting]: [],
        [ReleaseStepType.BetaTesting]: [],
        [ReleaseStepType.Metadata]: [],
        [ReleaseStepType.Translations]: [],
        [ReleaseStepType.Screenshots]: [],
        [ReleaseStepType.Approvals]: [],
        [ReleaseStepType.Submission]: [],
        [ReleaseStepType.StoreReview]: [],
        [ReleaseStepType.Takeoff]: [],
        [ReleaseStepType.CIDistribution]: [],
        [ReleaseStepType.VCSDistribution]: [],
        [ReleaseStepType.RollbackBuild]: [],
        [ReleaseStepType.Rollout]: [],
        ['none']: [],
    };
    sortedChecklistItems.forEach((cli) => {
        const releaseStepCLIs = groupedChecklistItems[cli.releaseStepType ?? 'none'];
        releaseStepCLIs.push(cli);
        if (groupedChecklistItems[cli.releaseStepType ?? 'none'].length > 5) {
            cli.disabled = true;
            return;
        }
        cli.disabled = false;
    });

    // sort by disabled state to display enabled items first
    checklistItems.sort((item1, item2) => (item1.disabled < item2.disabled ? -1 : 1));

    const platform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));

    const items = checklistItems.map((item) => {
        return {
            ...item,
            key: item.id,
        };
    });

    const [
        deleteChecklistItemTemplateCollection,
        deleteChecklistItemTemplateIsLoadingCollection,
        deleteChecklistItemTemplateErrorCollection,
    ] = useCallUpdatableForCollections(
        StepConfigUpdatable.DeleteChecklistItemTemplate,
        items.reduce<Record<string, ChecklistItemTemplateEntityRequest>>((acc, item) => {
            acc[item.id] = {
                orgId: props.orgId,
                workflowId,
                stepConfigId: item.stepConfigId,
                checklistItemId: item.id,
            };
            return acc;
        }, {}),
        items.map((item) => item.id),
    );

    const releaseStepItemDeleteIsLoading = useSelector((state: RootState) => {
        const releaseStepItemDeleteIsLoading: NumberKeyedDictionary<boolean> = {};
        forEach(checklistItems, (item) => {
            const isDeleteLoading = getIsLoading(AppSettingsV2Updatable.DeleteReleaseStepChecklistItem, state, item.id);

            releaseStepItemDeleteIsLoading[item.id] = isDeleteLoading;
        });

        return releaseStepItemDeleteIsLoading;
    }, shallowEqual);

    const releaseStepItemDeleteError = useSelector((state: RootState) => {
        const releaseStepItemDeleteError: NumberKeyedDictionary<string> = {};
        forEach(checklistItems, (item) => {
            const deleteError = getError(AppSettingsV2Updatable.DeleteReleaseStepChecklistItem, state, item.id);
            releaseStepItemDeleteError[item.id] = deleteError;
        });
        return releaseStepItemDeleteError;
    }, shallowEqual);

    const cliOwners = useSelector((state: RootState) => {
        let cliOwners: User[] = [];
        forEach(checklistItems, (item) => {
            const owners = selectCLIUsers(state, props.orgId, props.appId, item.owners);
            cliOwners = unionWith(cliOwners, owners, isEqual);
        });

        return cliOwners;
    }, shallowEqual);

    const [showNewItemModal, setShowNewItemModal] = React.useState(false);
    const [showEditItemModalWithId, setShowEditItemModalWithId] = React.useState<string | null>(null);
    const [filteredReleaseStepType, setFilteredReleaseStepType] = React.useState<ReleaseStepType | null>(null);
    const [showItemDescription, setShowItemDescription] = React.useState(false);
    const [itemInfo, setItemInfo] = React.useState<ChecklistItemInfo | null>(null);

    // This is a workaround, we shouldn't be re-setting the item status depending on itemApprovedUpdateError since it's an object and its reference can change. this is a product of the component being a functional component now, this is fixed for workflows stuff though. So this won't be necessary after all steps are migrated to workflows
    const errorValues = Object.values(releaseStepItemDeleteError);
    React.useEffect(() => {
        forIn(releaseStepItemDeleteError, (error) => {
            if (error) {
                globalMessage.error(error);
            }
        });
    }, [JSON.stringify(errorValues)]);

    React.useEffect(() => {
        for (const error of Object.values(deleteChecklistItemTemplateErrorCollection)) {
            if (error) {
                globalMessage.error(error);
            }
        }
    }, [JSON.stringify(deleteChecklistItemTemplateErrorCollection)]);

    const addNewChecklistItem = () => {
        setShowNewItemModal(true);
    };

    const deleteItem = (id: string) => {
        const requestData: ReleaseStepDeleteChecklistItemRequest = {
            orgId: props.orgId,
            appId: props.appId,
            checklistItemId: id,
        };

        const item = items.find((item) => item.id === id);
        if (item?.stepConfigId) {
            deleteChecklistItemTemplateCollection[item.id]();
        } else {
            dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.DeleteReleaseStepChecklistItem, requestData, id));
        }
    };

    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, props.appId));

    const dataSource = React.useMemo(() => {
        return items.filter((item) => {
            if (item.stepConfigId) {
                const stepConfig = stepGraphStepsMap[item.stepConfigId];
                return filteredReleaseStepType ? stepConfig?.archetype === filteredReleaseStepType : !!stepConfig;
            }

            return filteredReleaseStepType ? item.releaseStepType === filteredReleaseStepType : item.releaseStepType;
        });
    }, [items, stepGraphStepsMap, filteredReleaseStepType]);

    const shouldDisable = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return item.disabled && showRestrictedUI;
    };

    const textColor = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return shouldDisable(item, showRestrictedUI) ? antGrey.grey6 : antGrey.grey8;
    };

    const opacity = (item: ChecklistItemData, showRestrictedUI: boolean) => {
        return shouldDisable(item, showRestrictedUI) ? '30%' : '100%';
    };

    const renderTitle = (item: ChecklistItemData) => {
        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => {
                    if (item.stepConfigId) {
                        const stepConfig = stepGraphStepsMap[item.stepConfigId];
                        if (!stepConfig) return null;
                        return (
                            <InlineStepConfigDescription
                                stepGraphStep={stepConfig}
                                platform={platform}
                                appStoreType={appStoreType}
                                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                            />
                        );
                    }
                    return (
                        <Text style={{ color: textColor(item, isDisabled) }}>
                            {displayTextForReleaseStepType(
                                item.releaseStepType,
                                platform,
                                appStoreType,
                                isWorkflowsCIStepEnabled,
                            )}
                        </Text>
                    );
                }}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderDescription = (item: ChecklistItemData) => {
        return (
            <Space size={0} align="center">
                <div
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        paddingRight: 8,
                    }}
                >
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                        {(isDisabled) => (
                            <Paragraph
                                ellipsis={{ rows: 2 }}
                                style={{
                                    color: textColor(item, isDisabled),
                                    marginBottom: 0,
                                    flexShrink: 1,
                                }}
                            >
                                {!!item.orgTemplateId && orgLevelCLIIcon(props.orgId)}
                                {item.title}
                            </Paragraph>
                        )}
                    </PlanRestrictedFeatureWrapper>
                </div>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => (
                        <TooltipInfoIcon
                            tooltipContent={
                                shouldDisable(item, isDisabled) ? null : (
                                    <Space direction="vertical" style={{ padding: 6 }}>
                                        <Text style={{ color: 'white' }}>Identifier:</Text>
                                        <Text
                                            style={{
                                                backgroundColor: antGrey.grey5,
                                                color: runwayColors.darkTextGrey,
                                                borderRadius: 3,
                                                borderStyle: 'solid',
                                                borderColor: antGrey.grey7,
                                                borderWidth: 1,
                                                padding: '2px 4px',
                                            }}
                                        >
                                            {item.uid}
                                        </Text>
                                    </Space>
                                )
                            }
                            style={{ opacity: opacity(item, isDisabled) }}
                            hasCustomContent
                        />
                    )}
                </PlanRestrictedFeatureWrapper>
            </Space>
        );
    };

    const renderAdditionalInfoButton = (item) => {
        if (item.description) {
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => (
                        <Button
                            disabled={shouldDisable(item, isDisabled)}
                            onClick={() => {
                                setShowItemDescription(true);
                                setItemInfo({
                                    title: item.title,
                                    description: item.description,
                                });
                            }}
                            type="link"
                        >
                            View
                        </Button>
                    )}
                </PlanRestrictedFeatureWrapper>
            );
        } else {
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                    {(isDisabled) => <Text style={{ color: textColor(item, isDisabled) }}>-</Text>}
                </PlanRestrictedFeatureWrapper>
            );
        }
    };

    const renderActions = (item: ChecklistItemData) => {
        const isStepConfigItem = !!item.stepConfigId;

        const singleItemDeleteIsLoading =
            isStepConfigItem ?
                deleteChecklistItemTemplateIsLoadingCollection[item.id]
            :   releaseStepItemDeleteIsLoading[item.id];

        return (
            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                {(isDisabled) => (
                    <CLIEditActions
                        orgId={props.orgId}
                        itemId={item.id}
                        deleteIsLoading={singleItemDeleteIsLoading}
                        onConfirmDelete={deleteItem}
                        onEditChecklistItem={(id) => setShowEditItemModalWithId(id)}
                        disableEditButton={shouldDisable(item, isDisabled)}
                        shouldDisableOrgLevelCliActions={!!item.orgTemplateId}
                    />
                )}
            </PlanRestrictedFeatureWrapper>
        );
    };

    const renderColumnTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const showHideCreateEditModal = () => {
        setShowEditItemModalWithId(null);
        setShowNewItemModal(false);
    };

    const columns: ColumnType<ChecklistItemTemplate>[] = [
        {
            title: renderColumnTitle('Release step'),
            render: renderTitle,
        },
        {
            title: renderColumnTitle('Checklist item'),
            render: renderDescription,
        },
        {
            title: renderColumnTitle('Additional info'),
            align: 'center',
            render: renderAdditionalInfoButton,
        },
        {
            title: renderColumnTitle('Owners'),
            dataIndex: 'approverGroups',
            align: 'center',
            render: (approverGroups: UserGroupId[], cli) => {
                if (!approverGroups) {
                    return null;
                }
                if (approverGroups.length > 0) {
                    const groups = approverGroups.map((groupId) => orgGroups[groupId]);
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => (
                                <Text style={{ color: textColor(cli, isDisabled) }}>
                                    {displayTextForUserChecklistGroups(groups)}
                                </Text>
                            )}
                        </PlanRestrictedFeatureWrapper>
                    );
                } else if (cli.owners.length > 0) {
                    const cliOwnersMapped = cli.owners
                        .map((userId) => cliOwners.find((owner) => owner.id === userId))
                        .filter((owner): owner is User => owner !== undefined);
                    if (cliOwnersMapped.length === 0) {
                        return (
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                                {(isDisabled) => <Text style={{ color: textColor(cli, isDisabled) }}>No owners</Text>}
                            </PlanRestrictedFeatureWrapper>
                        );
                    }
                    if (cliOwnersMapped.length > 1) {
                        return (
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                                {(isDisabled) => (
                                    <OwnerAvatarGroup
                                        maxAvatars={2}
                                        owners={cliOwnersMapped}
                                        isDisabled={shouldDisable(cli, isDisabled)}
                                    />
                                )}
                            </PlanRestrictedFeatureWrapper>
                        );
                    } else {
                        const fullName = displayUserNameLong(cliOwnersMapped[0]);
                        const ownerAvatar = (isDisabled: boolean) => (
                            <div>
                                <OwnerAvatar user={cliOwnersMapped[0]} isDisabled={shouldDisable(cli, isDisabled)} />
                            </div>
                        );
                        return (
                            <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                                {(isDisabled) =>
                                    isDisabled ?
                                        <>{ownerAvatar(shouldDisable(cli, isDisabled))}</>
                                    :   <Tooltip key={cliOwnersMapped[0].id} title={fullName} placement="top">
                                            <>{ownerAvatar(false)}</>
                                        </Tooltip>
                                }
                            </PlanRestrictedFeatureWrapper>
                        );
                    }
                } else {
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => <Text style={{ color: textColor(cli, isDisabled) }}>No owners</Text>}
                        </PlanRestrictedFeatureWrapper>
                    );
                }
            },
        },
        {
            title: renderColumnTitle('Release types'),
            dataIndex: 'releaseTypes',
            align: 'center',
            render: (releaseTypes: ChecklistItemReleaseTypes, cli) => {
                const renderReleaseTypeText = (text: string) => {
                    return (
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI}>
                            {(isDisabled) => <Text style={{ color: textColor(cli, isDisabled) }}>{text}</Text>}
                        </PlanRestrictedFeatureWrapper>
                    );
                };
                switch (releaseTypes) {
                    case ChecklistItemReleaseTypes.All:
                        return renderReleaseTypeText('All types');
                    case ChecklistItemReleaseTypes.AllExceptHotfixAndRollback:
                        return renderReleaseTypeText('All types except hotfixes');
                    case ChecklistItemReleaseTypes.HotfixOnly:
                        return renderReleaseTypeText('Hotfixes only');
                    case ChecklistItemReleaseTypes.RollbackOnly:
                        return renderReleaseTypeText('Rollbacks only');
                }
            },
        },
        {
            title: '',
            align: 'right',
            render: renderActions,
        },
    ];

    return (
        <div ref={props.refProp}>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <h4 style={styles.appSettingsSectionTitleStyle}>Checklist items</h4>
                        <div>
                            <Select
                                placeholder="Filter by release step"
                                onChange={(value) => setFilteredReleaseStepType(value as ReleaseStepType)}
                                style={{ minWidth: 200 }}
                            >
                                <Select.Option key={0} value={null}>
                                    All release steps
                                </Select.Option>
                                {releaseSteps.map((releaseStepType, index) => {
                                    return (
                                        <Select.Option key={index + 1} value={releaseStepType}>
                                            {displayTextForReleaseStepType(
                                                releaseStepType,
                                                platform,
                                                appStoreType,
                                                isWorkflowsCIStepEnabled,
                                            )}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                    <Space>
                        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.FeatureCLI} requiresTooltip>
                            <Button style={{ float: 'right' }} icon={<PlusOutlined />} onClick={addNewChecklistItem}>
                                Add
                            </Button>
                        </PlanRestrictedFeatureWrapper>
                    </Space>
                </div>

                <Table<ChecklistItemTemplate>
                    style={{ marginTop: 24, marginBottom: 12 }}
                    size="middle"
                    locale={{
                        emptyText: 'No checklist items added yet',
                    }}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                />
                <CreateUpdateChecklistItemModal
                    orgId={props.orgId}
                    appId={props.appId}
                    open={showNewItemModal || showEditItemModalWithId !== null}
                    isEditingItemWithId={showEditItemModalWithId}
                    onCancel={showHideCreateEditModal}
                    onSuccess={showHideCreateEditModal}
                    releaseStepTypes={releaseSteps}
                    placement={ChecklistItemPlacement.StepChecklist}
                    stepRunComponentProps={null}
                    releaseStepType={filteredReleaseStepType}
                    isOrgLevelTemplate={false}
                />
                <ChecklistItemAdditionalInfoModal
                    title={itemInfo?.title ?? ''}
                    description={itemInfo?.description ?? ''}
                    isVisible={!!showItemDescription}
                    onCancel={() => setShowItemDescription(false)}
                    afterClose={() => setItemInfo(null)}
                />
            </div>
        </div>
    );
};
